import { alpha, createTheme, type PaletteMode } from '@mui/material/styles';
import { darkColors, lightColors, radii } from '@mh-wms/design-tokens';
import type { TableThemePreset } from './tableTheme';

const tablePresets: Record<
  TableThemePreset,
  { cellPadding: number; fontSize: string; striped: boolean; verticalLines: boolean; strongHeader: boolean }
> = {
  modern: { cellPadding: 10, fontSize: '0.875rem', striped: false, verticalLines: false, strongHeader: true },
  compact: { cellPadding: 6, fontSize: '0.8rem', striped: false, verticalLines: false, strongHeader: false },
  banded: { cellPadding: 9, fontSize: '0.875rem', striped: true, verticalLines: false, strongHeader: false },
  grid: { cellPadding: 8, fontSize: '0.84rem', striped: false, verticalLines: true, strongHeader: true },
};

/** Semantic, reference-aligned theme for data-dense warehouse operations. */
export function createAppTheme(mode: PaletteMode, tablePreset: TableThemePreset = 'modern') {
  const color = mode === 'dark' ? darkColors : lightColors;
  const table = tablePresets[tablePreset];
  const headerBackground = table.strongHeader
    ? alpha(color.primary, mode === 'dark' ? 0.18 : 0.09)
    : mode === 'dark'
      ? color.surfaceElevated
      : '#f7f8fa';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: color.primary,
        dark: color.primaryHover,
        contrastText: mode === 'dark' ? '#0d1b2a' : '#ffffff',
      },
      background: { default: color.background, paper: color.surface },
      text: { primary: color.textPrimary, secondary: color.textSecondary },
      divider: color.border,
      success: { main: color.success },
      warning: { main: color.warning },
      error: { main: color.danger },
      info: { main: color.info },
    },
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
      fontSize: 14,
      h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25 },
      h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: radii.sm },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { minWidth: 320 },
          '*:focus-visible': { outline: `3px solid ${color.focusRing}`, outlineOffset: 2 },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              scrollBehavior: 'auto !important',
              transitionDuration: '0.01ms !important',
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
            },
          },
        },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiIconButton: { styleOverrides: { root: { minWidth: 44, minHeight: 44 } } },
      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
      MuiAutocomplete: { defaultProps: { size: 'small' } },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { rounded: { borderRadius: radii.md } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: color.border,
            borderRight: table.verticalLines ? `1px solid ${color.border}` : undefined,
            fontVariantNumeric: 'tabular-nums',
            fontSize: table.fontSize,
            lineHeight: 1.45,
            padding: `${table.cellPadding}px 12px`,
            overflowWrap: 'anywhere',
            '&:last-child': { borderRight: 0 },
          },
          head: {
            backgroundColor: headerBackground,
            color: table.strongHeader ? color.textPrimary : color.textSecondary,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: tablePreset === 'compact' ? '0.025em' : 0,
            textTransform: tablePreset === 'compact' ? 'uppercase' : 'none',
            borderBottomWidth: table.strongHeader ? 2 : 1,
            borderBottomColor: table.strongHeader ? color.primary : color.border,
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: table.striped
            ? {
                '& .MuiTableRow-root:nth-of-type(even)': {
                  backgroundColor: alpha(color.primary, mode === 'dark' ? 0.07 : 0.035),
                },
              }
            : {},
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 150ms ease',
            '&.MuiTableRow-hover:hover': {
              backgroundColor: `${alpha(color.primary, mode === 'dark' ? 0.16 : 0.07)} !important`,
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          toolbar: { minHeight: tablePreset === 'compact' ? 46 : 52 },
        },
      },
      MuiTooltip: { defaultProps: { arrow: true } },
    },
  });
}
